import type {
  MarkContext,
  MarkPanelSession,
  MarkPanelState,
  MarkSurfaceId,
} from './mark-panel.types';

export type MarkPanelReducerAction =
  | {
      type: 'surfaceRegistered';
      payload: { id: MarkSurfaceId; clientId: string };
    }
  | {
      type: 'surfaceUnregistered';
      payload: { id: MarkSurfaceId; clientId: string };
    }
  | { type: 'opened'; payload: { session: MarkPanelSession } }
  | { type: 'focusedExisting' }
  | { type: 'closed' }
  | {
      type: 'conversationBound';
      payload: { sessionKey: string; conversationId: string };
    };

export const initialMarkPanelState: MarkPanelState = {
  status: 'closed',
  session: null,
  activeSurface: null,
  pendingOpen: null,
};

const isSameSurface = (
  surface: { id: MarkSurfaceId; clientId: string } | null,
  target: { id: MarkSurfaceId; clientId: string }
): boolean =>
  surface !== null &&
  surface.id === target.id &&
  surface.clientId === target.clientId;

export const markPanelReducer = (
  state: MarkPanelState,
  action: MarkPanelReducerAction
): MarkPanelState => {
  switch (action.type) {
    case 'surfaceRegistered':
      return { ...state, activeSurface: action.payload };

    case 'surfaceUnregistered': {
      const sessionBelongsToSurface =
        state.session !== null &&
        state.session.surfaceId === action.payload.id &&
        state.session.clientId === action.payload.clientId;

      return {
        ...state,
        activeSurface: isSameSurface(state.activeSurface, action.payload)
          ? null
          : state.activeSurface,
        status: sessionBelongsToSurface ? 'closed' : state.status,
        session: sessionBelongsToSurface ? null : state.session,
        pendingOpen: sessionBelongsToSurface ? null : state.pendingOpen,
      };
    }

    case 'opened':
      return { ...state, status: 'docked', session: action.payload.session };

    case 'focusedExisting':
      return { ...state, status: 'docked' };

    case 'closed':
      return { ...state, status: 'closed', session: null, pendingOpen: null };

    case 'conversationBound': {
      if (
        state.session === null ||
        state.session.key !== action.payload.sessionKey
      ) {
        return state;
      }

      return {
        ...state,
        session: {
          ...state.session,
          conversationId: action.payload.conversationId,
        },
      };
    }

    default:
      return state;
  }
};

export const resolveOpenIntent = (
  state: MarkPanelState,
  context: MarkContext
): 'focusExisting' | 'startNewSession' =>
  state.status !== 'closed' && state.session?.context.key === context.key
    ? 'focusExisting'
    : 'startNewSession';
